<template>
  <div>
    <div class="p-6 bg-orange-light/40">
      <h1 class="text-2xl font-bold mb-1">
        Rapprochement des patients
      </h1>
      <p>
        Rapprochez les patients identiques pour consolider les données, notamment dans
        les cas suivants :
      </p>
      <ul class="list-disc pl-6">
        <li>Enfant rattaché à la fois au père et à la mère</li>
        <li>Veuf(ve) précédemment assuré(e) via le conjoint décédé</li>
      </ul>
      <div class="mt-4 text-primary">
        <router-link to="/consumer-reconciliations/index" class="flex items-center">
          <ArrowRight class="w-4 mr-1" />
          Voir les patients déjà rapprochés
        </router-link>
      </div>
    </div>
    <div v-if="isLoading" class="flex h-full justify-center items-center">
      <BaseFlexSpinner size="medium-small" />
    </div>
    <BaseCard v-else>
      <BaseCardHeader>
        <div class="flex items-center gap-4">
          <div class="flex items-center gap-2 border-r pr-4">
            <CopyCheck class="text-primary" />
            {{ data?.data.length }}{{ data?.links?.next ? '+' : '' }} candidats au rapprochement
          </div>
          <div class="border-r pr-4">
            <CustomerSearch
              class="w-full max-w-80 bg-gray-50"
              :callback="searchConsumer"
              placeholder="Rechercher un patient ..."
              @clear="parameters.consumer_key = undefined"
            />
          </div>
          <div class="flex flex-1 justify-end items-center gap-2">
            <Label for="load-sales" class="text-sm">Charger les ventes</Label>
            <Switch id="load-sales" v-model="parameters.with_sales" />
          </div>
        </div>
      </BaseCardHeader>
      <div v-if="isValidating" class="flex h-full justify-center items-center py-16">
        <BaseFlexSpinner size="medium-small" />
      </div>
      <div v-else class="flex flex-col p-6">
        <div v-for="(candidate, index) in data?.data" :key="index">
          <AnimatePresence>
            <motion.div
              v-if="!hiddenCandidateIds.includes(candidate.id)"
              :key="`candidate-${candidate.consumer_keys.join('-')}`"
              :initial="{ opacity: 1, x: 0 }"
              :exit="{ opacity: 0, x: -200 }"
              :transition="{ duration: 0.25, type: 'tween' }"
            >
              <ReconciliationCandidateRow :candidate="candidate" @hide="hiddenCandidateIds.push(candidate.id)" />
            </motion.div>
          </AnimatePresence>
        </div>
        <div class="flex justify-center items-center gap-4 pt-4">
          <Button variant="outline" size="sm" :disabled="(parameters.page ?? 1) <= 1" @click="previousPage">
            <ChevronLeft class="w-4" />
            Précédent
          </Button>
          <span class="text-sm text-gray-500">Page {{ parameters.page }}</span>
          <Button variant="outline" size="sm" :disabled="!data?.links?.next" @click="nextPage">
            Suivant
            <ChevronRight class="w-4" />
          </Button>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script setup lang="ts">
import useSwr from "@/core/composables/swr/useSwr";
import {computed, reactive, ref, watch} from "vue";
import {cacheKey} from "@/lib/cache";
import {reconciliationApi} from "@/container";
import ReconciliationCandidateRow from "@/consumer-reconciliation/components/ReconciliationCandidateRow.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardHeader from "@/core/components/base/BaseCardHeader.vue";
import {ArrowRight, ChevronLeft, ChevronRight, CopyCheck} from "@lucide/vue";
import {motion, AnimatePresence} from "motion-v";
import CustomerSearch from "@/customer/components/customer-search/CustomerSearch.vue";
import {Switch} from "@/core/components/ui/switch";
import {Label} from "@/core/components/ui/label";
import {Button} from "@/core/components/ui/button";
import {ReconciliationIndexRequest} from "@/core/http/resources/consumer-reconciliation/ReconciliationResource";

const hiddenCandidateIds = ref<string[]>([]);

const parameters = reactive<ReconciliationIndexRequest>({
    consumer_key: undefined,
    with_sales: undefined,
    page: 1
});

watch(() => [parameters.consumer_key, parameters.with_sales], () => {
    parameters.page = 1;
});

const previousPage = () => {
    parameters.page = Math.max(1, (parameters.page ?? 1) - 1);
};

const nextPage = () => {
    parameters.page = (parameters.page ?? 1) + 1;
};

/**
 * Search for a consumer key.
 *
 * @param type  The consumer type : beneficiary or customer.
 * @param _id
 * @param key The consumer key.
 */
const searchConsumer = (type: 'beneficiary' | 'customer' | null, _id?: string, key?: string) => {
    parameters.consumer_key = type && key ? key : undefined;
};

/**
 * Fetches reconciliation candidates.
 */
const {data, isLoading, isValidating} = useSwr(
    computed(() => cacheKey(`consumer-reconciliations/candidates`, parameters)),
    () => reconciliationApi().candidates(parameters),
);
</script>
